import { PackageProviderName, SettingKey } from 'data/ppoints/constants';
import { useAppStatusSWR } from 'features/settings/components/ppoints/dashboard/app-status/hooks/useAppStatusSWR';
import { updateAppStatus } from 'features/settings/components/ppoints/dashboard/app-status/mutations/app-status-mutations';
import { useUpdateIntercomFields } from 'hooks/intercom/useUpdateIntercomFields';
import useSettings from 'hooks/useSettings';
import { ProviderToCountrySetting } from 'models/ppoints/settings/SettingsToEcom';
import { useCallback, useState } from 'react';
import { useAppSelector } from 'redux/hooks';

export interface KeepProvidersWithinLimit {
    /**
     * Deactivates every carrier not in `providersToKeep` (in every country) and then activates the app.
     * Rejects when either request fails, so the caller can show an error.
     */
    keepProviders: (providersToKeep: PackageProviderName[]) => Promise<void>;
    isSaving: boolean;
}

/**
 * The "choose which carriers to keep" step after a plan downgrade left more carriers active than the
 * new plan allows. Mirrors `useProviderActivation.deactivateProvider`'s side effects (settings write +
 * Intercom fields), then re-activates the app that the downgrade had deactivated.
 */
const useKeepProvidersWithinLimit = (): KeepProvidersWithinLimit => {
    const { settings: providerToCountry, setSettingsWithTypeReturn: setProviderToCountry } =
        useSettings<ProviderToCountrySetting>(SettingKey.provider_to_country);
    const { handleProviderToCountryToIntercom } = useUpdateIntercomFields();
    const packageProviders = useAppSelector((state) => state.packageProviders.packageProviders);
    const { mutate: mutateAppStatus } = useAppStatusSWR();

    const [isSaving, setIsSaving] = useState(false);

    const keepProviders = useCallback(
        async (providersToKeep: PackageProviderName[]) => {
            setIsSaving(true);
            try {
                const nextSettings: ProviderToCountrySetting = Object.fromEntries(
                    Object.entries(providerToCountry ?? {}).map(([country, providers]) => [
                        country,
                        (providers ?? []).filter((provider) => providersToKeep.includes(provider)),
                    ])
                );

                const saved = await setProviderToCountry(nextSettings);
                if (!saved) {
                    throw new Error('Failed to save provider_to_country');
                }
                handleProviderToCountryToIntercom(saved, packageProviders);

                await updateAppStatus(true);
                await mutateAppStatus();
            } finally {
                setIsSaving(false);
            }
        },
        [providerToCountry, setProviderToCountry, handleProviderToCountryToIntercom, packageProviders, mutateAppStatus]
    );

    return { keepProviders, isSaving };
};

export default useKeepProvidersWithinLimit;
